// Copyright (c) Microsoft Corporation.
// Licensed under the MIT License.

// Code generated by Microsoft (R) AutoRest Code Generator.
// Changes may cause incorrect behavior and will be lost if the code is regenerated.

import type { PagedAsyncIterableIterator, PageSettings } from "@azure/core-paging";
import { setContinuationToken } from "../pagingHelper.js";
import type { VirtualHubBgpConnections } from "../operationsInterfaces/index.js";
import * as coreClient from "@azure/core-client";
import * as Mappers from "../models/mappers.js";
import * as Parameters from "../models/parameters.js";
import type { NetworkManagementClient } from "../networkManagementClient.js";
import type { SimplePollerLike, OperationState } from "@azure/core-lro";
import { createHttpPoller } from "@azure/core-lro";
import { createLroSpec } from "../lroImpl.js";
import type {
  BgpConnection,
  VirtualHubBgpConnectionsListNextOptionalParams,
  VirtualHubBgpConnectionsListOptionalParams,
  VirtualHubBgpConnectionsListResponse,
  VirtualHubBgpConnectionsListLearnedRoutesOptionalParams,
  VirtualHubBgpConnectionsListLearnedRoutesResponse,
  VirtualHubBgpConnectionsListAdvertisedRoutesOptionalParams,
  VirtualHubBgpConnectionsListAdvertisedRoutesResponse,
  VirtualHubBgpConnectionsListNextResponse,
} from "../models/index.js";

/// <reference lib="esnext.asynciterable" />
/** Class containing VirtualHubBgpConnections operations. */
export class VirtualHubBgpConnectionsImpl implements VirtualHubBgpConnections {
  private readonly client: NetworkManagementClient;

  /**
   * Initialize a new instance of the class VirtualHubBgpConnections class.
   * @param client Reference to the service client
   */
  constructor(client: NetworkManagementClient) {
    this.client = client;
  }

  /**
   * Retrieves the details of all VirtualHubBgpConnections.
   * @param resourceGroupName The resource group name of the VirtualHub.
   * @param virtualHubName The name of the VirtualHub.
   * @param options The options parameters.
   */
  public list(
    resourceGroupName: string,
    virtualHubName: string,
    options?: VirtualHubBgpConnectionsListOptionalParams,
  ): PagedAsyncIterableIterator<BgpConnection> {
    const iter = this.listPagingAll(resourceGroupName, virtualHubName, options);
    return {
      next() {
        return iter.next();
      },
      [Symbol.asyncIterator]() {
        return this;
      },
      byPage: (settings?: PageSettings) => {
        if (settings?.maxPageSize) {
          throw new Error("maxPageSize is not supported by this operation.");
        }
        return this.listPagingPage(resourceGroupName, virtualHubName, options, settings);
      },
    };
  }

  private async *listPagingPage(
    resourceGroupName: string,
    virtualHubName: string,
    options?: VirtualHubBgpConnectionsListOptionalParams,
    settings?: PageSettings,
  ): AsyncIterableIterator<BgpConnection[]> {
    let result: VirtualHubBgpConnectionsListResponse;
    let continuationToken = settings?.continuationToken;
    if (!continuationToken) {
      result = await this._list(resourceGroupName, virtualHubName, options);
      const page = result.value || [];
      continuationToken = result.nextLink;
      setContinuationToken(page, continuationToken);
      yield page;
    }
    while (continuationToken) {
      result = await this._listNext(resourceGroupName, virtualHubName, continuationToken, options);
      continuationToken = result.nextLink;
      const page = result.value || [];
      setContinuationToken(page, continuationToken);
      yield page;
    }
  }

  private async *listPagingAll(
    resourceGroupName: string,
    virtualHubName: string,
    options?: VirtualHubBgpConnectionsListOptionalParams,
  ): AsyncIterableIterator<BgpConnection> {
    for await (const page of this.listPagingPage(resourceGroupName, virtualHubName, options)) {
      yield* page;
    }
  }

  /**
   * Retrieves the details of all VirtualHubBgpConnections.
   * @param resourceGroupName The resource group name of the VirtualHub.
   * @param virtualHubName The name of the VirtualHub.
   * @param options The options parameters.
   */
  private _list(
    resourceGroupName: string,
    virtualHubName: string,
    options?: VirtualHubBgpConnectionsListOptionalParams,
  ): Promise<VirtualHubBgpConnectionsListResponse> {
    return this.client.sendOperationRequest(
      { resourceGroupName, virtualHubName, options },
      listOperationSpec,
    );
  }

  /**
   * Retrieves a list of routes the virtual hub bgp connection has learned.
   * @param resourceGroupName The name of the resource group.
   * @param hubName The name of the virtual hub.
   * @param connectionName The name of the virtual hub bgp connection.
   * @param options The options parameters.
   */
  async beginListLearnedRoutes(
    resourceGroupName: string,
    hubName: string,
    connectionName: string,
    options?: VirtualHubBgpConnectionsListLearnedRoutesOptionalParams,
  ): Promise<
    SimplePollerLike<
      OperationState<VirtualHubBgpConnectionsListLearnedRoutesResponse>,
      VirtualHubBgpConnectionsListLearnedRoutesResponse
    >
  > {
    const directSendOperation = async (
      args: coreClient.OperationArguments,
      spec: coreClient.OperationSpec,
    ): Promise<VirtualHubBgpConnectionsListLearnedRoutesResponse> => {
      return this.client.sendOperationRequest(args, spec);
    };
    const sendOperationFn = async (
      args: coreClient.OperationArguments,
      spec: coreClient.OperationSpec,
    ) => {
      let currentRawResponse: coreClient.FullOperationResponse | undefined = undefined;
      const providedCallback = args.options?.onResponse;
      const callback: coreClient.RawResponseCallback = (
        rawResponse: coreClient.FullOperationResponse,
        flatResponse: unknown,
      ) => {
        currentRawResponse = rawResponse;
        providedCallback?.(rawResponse, flatResponse);
      };
      const updatedArgs = {
        ...args,
        options: {
          ...args.options,
          onResponse: callback,
        },
      };
      const flatResponse = await directSendOperation(updatedArgs, spec);
      return {
        flatResponse,
        rawResponse: {
          statusCode: currentRawResponse!.status,
          body: currentRawResponse!.parsedBody,
          headers: currentRawResponse!.headers.toJSON(),
        },
      };
    };

    const lro = createLroSpec({
      sendOperationFn,
      args: { resourceGroupName, hubName, connectionName, options },
      spec: listLearnedRoutesOperationSpec,
    });
    const poller = await createHttpPoller<
      VirtualHubBgpConnectionsListLearnedRoutesResponse,
      OperationState<VirtualHubBgpConnectionsListLearnedRoutesResponse>
    >(lro, {
      restoreFrom: options?.resumeFrom,
      intervalInMs: options?.updateIntervalInMs,
      resourceLocationConfig: "location",
    });
    await poller.poll();
    return poller;
  }

  /**
   * Retrieves a list of routes the virtual hub bgp connection has learned.
   * @param resourceGroupName The name of the resource group.
   * @param hubName The name of the virtual hub.
   * @param connectionName The name of the virtual hub bgp connection.
   * @param options The options parameters.
   */
  async beginListLearnedRoutesAndWait(
    resourceGroupName: string,
    hubName: string,
    connectionName: string,
    options?: VirtualHubBgpConnectionsListLearnedRoutesOptionalParams,
  ): Promise<VirtualHubBgpConnectionsListLearnedRoutesResponse> {
    const poller = await this.beginListLearnedRoutes(
      resourceGroupName,
      hubName,
      connectionName,
      options,
    );
    return poller.pollUntilDone();
  }

  /**
   * Retrieves a list of routes the virtual hub bgp connection is advertising to the specified peer.
   * @param resourceGroupName The name of the resource group.
   * @param hubName The name of the virtual hub.
   * @param connectionName The name of the virtual hub bgp connection.
   * @param options The options parameters.
   */
  async beginListAdvertisedRoutes(
    resourceGroupName: string,
    hubName: string,
    connectionName: string,
    options?: VirtualHubBgpConnectionsListAdvertisedRoutesOptionalParams,
  ): Promise<
    SimplePollerLike<
      OperationState<VirtualHubBgpConnectionsListAdvertisedRoutesResponse>,
      VirtualHubBgpConnectionsListAdvertisedRoutesResponse
    >
  > {
    const directSendOperation = async (
      args: coreClient.OperationArguments,
      spec: coreClient.OperationSpec,
    ): Promise<VirtualHubBgpConnectionsListAdvertisedRoutesResponse> => {
      return this.client.sendOperationRequest(args, spec);
    };
    const sendOperationFn = async (
      args: coreClient.OperationArguments,
      spec: coreClient.OperationSpec,
    ) => {
      let currentRawResponse: coreClient.FullOperationResponse | undefined = undefined;
      const providedCallback = args.options?.onResponse;
      const callback: coreClient.RawResponseCallback = (
        rawResponse: coreClient.FullOperationResponse,
        flatResponse: unknown,
      ) => {
        currentRawResponse = rawResponse;
        providedCallback?.(rawResponse, flatResponse);
      };
      const updatedArgs = {
        ...args,
        options: {
          ...args.options,
          onResponse: callback,
        },
      };
      const flatResponse = await directSendOperation(updatedArgs, spec);
      return {
        flatResponse,
        rawResponse: {
          statusCode: currentRawResponse!.status,
          body: currentRawResponse!.parsedBody,
          headers: currentRawResponse!.headers.toJSON(),
        },
      };
    };

    const lro = createLroSpec({
      sendOperationFn,
      args: { resourceGroupName, hubName, connectionName, options },
      spec: listAdvertisedRoutesOperationSpec,
    });
    const poller = await createHttpPoller<
      VirtualHubBgpConnectionsListAdvertisedRoutesResponse,
      OperationState<VirtualHubBgpConnectionsListAdvertisedRoutesResponse>
    >(lro, {
      restoreFrom: options?.resumeFrom,
      intervalInMs: options?.updateIntervalInMs,
      resourceLocationConfig: "location",
    });
    await poller.poll();
    return poller;
  }

  /**
   * Retrieves a list of routes the virtual hub bgp connection is advertising to the specified peer.
   * @param resourceGroupName The name of the resource group.
   * @param hubName The name of the virtual hub.
   * @param connectionName The name of the virtual hub bgp connection.
   * @param options The options parameters.
   */
  async beginListAdvertisedRoutesAndWait(
    resourceGroupName: string,
    hubName: string,
    connectionName: string,
    options?: VirtualHubBgpConnectionsListAdvertisedRoutesOptionalParams,
  ): Promise<VirtualHubBgpConnectionsListAdvertisedRoutesResponse> {
    const poller = await this.beginListAdvertisedRoutes(
      resourceGroupName,
      hubName,
      connectionName,
      options,
    );
    return poller.pollUntilDone();
  }

  /**
   * ListNext
   * @param resourceGroupName The resource group name of the VirtualHub.
   * @param virtualHubName The name of the VirtualHub.
   * @param nextLink The nextLink from the previous successful call to the List method.
   * @param options The options parameters.
   */
  private _listNext(
    resourceGroupName: string,
    virtualHubName: string,
    nextLink: string,
    options?: VirtualHubBgpConnectionsListNextOptionalParams,
  ): Promise<VirtualHubBgpConnectionsListNextResponse> {
    return this.client.sendOperationRequest(
      { resourceGroupName, virtualHubName, nextLink, options },
      listNextOperationSpec,
    );
  }
}
// Operation Specifications
const serializer = coreClient.createSerializer(Mappers, /* isXml */ false);

const listOperationSpec: coreClient.OperationSpec = {
  path: "/subscriptions/{subscriptionId}/resourceGroups/{resourceGroupName}/providers/Microsoft.Network/virtualHubs/{virtualHubName}/bgpConnections",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.ListVirtualHubBgpConnectionResults,
    },
    default: {
      bodyMapper: Mappers.CloudError,
    },
  },
  queryParameters: [Parameters.apiVersion],
  urlParameters: [
    Parameters.$host,
    Parameters.resourceGroupName,
    Parameters.subscriptionId,
    Parameters.virtualHubName,
  ],
  headerParameters: [Parameters.accept],
  serializer,
};
const listLearnedRoutesOperationSpec: coreClient.OperationSpec = {
  path: "/subscriptions/{subscriptionId}/resourceGroups/{resourceGroupName}/providers/Microsoft.Network/virtualHubs/{hubName}/bgpConnections/{connectionName}/learnedRoutes",
  httpMethod: "POST",
  responses: {
    200: {
      bodyMapper: {
        type: {
          name: "Dictionary",
          value: {
            type: {
              name: "Sequence",
              element: { type: { name: "Composite", className: "PeerRoute" } },
            },
          },
        },
      },
    },
    201: {
      bodyMapper: {
        type: {
          name: "Dictionary",
          value: {
            type: {
              name: "Sequence",
              element: { type: { name: "Composite", className: "PeerRoute" } },
            },
          },
        },
      },
    },
    202: {
      bodyMapper: {
        type: {
          name: "Dictionary",
          value: {
            type: {
              name: "Sequence",
              element: { type: { name: "Composite", className: "PeerRoute" } },
            },
          },
        },
      },
    },
    204: {
      bodyMapper: {
        type: {
          name: "Dictionary",
          value: {
            type: {
              name: "Sequence",
              element: { type: { name: "Composite", className: "PeerRoute" } },
            },
          },
        },
      },
    },
    default: {
      bodyMapper: Mappers.CloudError,
    },
  },
  queryParameters: [Parameters.apiVersion],
  urlParameters: [
    Parameters.$host,
    Parameters.resourceGroupName,
    Parameters.subscriptionId,
    Parameters.connectionName,
    Parameters.hubName,
  ],
  headerParameters: [Parameters.accept],
  serializer,
};
const listAdvertisedRoutesOperationSpec: coreClient.OperationSpec = {
  path: "/subscriptions/{subscriptionId}/resourceGroups/{resourceGroupName}/providers/Microsoft.Network/virtualHubs/{hubName}/bgpConnections/{connectionName}/advertisedRoutes",
  httpMethod: "POST",
  responses: {
    200: {
      bodyMapper: {
        type: {
          name: "Dictionary",
          value: {
            type: {
              name: "Sequence",
              element: { type: { name: "Composite", className: "PeerRoute" } },
            },
          },
        },
      },
    },
    201: {
      bodyMapper: {
        type: {
          name: "Dictionary",
          value: {
            type: {
              name: "Sequence",
              element: { type: { name: "Composite", className: "PeerRoute" } },
            },
          },
        },
      },
    },
    202: {
      bodyMapper: {
        type: {
          name: "Dictionary",
          value: {
            type: {
              name: "Sequence",
              element: { type: { name: "Composite", className: "PeerRoute" } },
            },
          },
        },
      },
    },
    204: {
      bodyMapper: {
        type: {
          name: "Dictionary",
          value: {
            type: {
              name: "Sequence",
              element: { type: { name: "Composite", className: "PeerRoute" } },
            },
          },
        },
      },
    },
    default: {
      bodyMapper: Mappers.CloudError,
    },
  },
  queryParameters: [Parameters.apiVersion],
  urlParameters: [
    Parameters.$host,
    Parameters.resourceGroupName,
    Parameters.subscriptionId,
    Parameters.connectionName,
    Parameters.hubName,
  ],
  headerParameters: [Parameters.accept],
  serializer,
};
const listNextOperationSpec: coreClient.OperationSpec = {
  path: "{nextLink}",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.ListVirtualHubBgpConnectionResults,
    },
    default: {
      bodyMapper: Mappers.CloudError,
    },
  },
  urlParameters: [
    Parameters.$host,
    Parameters.resourceGroupName,
    Parameters.subscriptionId,
    Parameters.nextLink,
    Parameters.virtualHubName,
  ],
  headerParameters: [Parameters.accept],
  serializer,
};
